/**
 * @file React hook — usePublicationData.ts: form/state logic for contributor dashboard.
 */

import { useEffect, useRef, useState } from "react";
import { FORM_STAGE_CODES } from "../../../../../../constants/formStages";
import {
  FormDataResponse,
  FormField,
  FormSubSection,
} from "../../../../../../interfaces/formInterfaces";
import {
  getPublicationFormData,
  getSubmission,
  savePublicationDetails,
} from "../../../../../../services/publicationService";
import { toast } from "react-toastify";
import { validatePublicationFileSize } from "../../../../../../constants/publicationFileUpload";
import { reverseGeocode } from "../../../../../../utils/useA1Location";
import {
  getFirstFormError,
  validatePublicationForm,
} from "../utils/validatePublicationForm";
import { clearFieldErrorsFromMap } from "../../shared/contributorFormValidation";

const shallowEqual = (a: Record<string, string>, b: Record<string, string>) => {
  const aKeys = Object.keys(a || {});
  const bKeys = Object.keys(b || {});
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
};

const fileFingerprint = (f: File | null | undefined) => {
  if (!f) return "";
  // Stable-ish identifier for "did the selected file change"
  return `${f.name}|${f.size}|${f.lastModified}|${f.type || ""}`;
};

const buildFilesSnapshot = (m: Record<string, File | null>) => {
  const snap: Record<string, string> = {};
  Object.keys(m || {}).forEach((k) => {
    const fp = fileFingerprint(m[k]);
    if (fp) snap[k] = fp;
  });
  return snap;
};

/** Hook: use Publication Data. */
export const usePublicationData = (submissionId?: string) => {
  const [formData, setFormData] = useState<FormDataResponse | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasSavedOnce, setHasSavedOnce] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const lastSavedValuesRef = useRef<Record<string, string>>({});
  const lastSavedFilesRef = useRef<Record<string, string>>({});

  /* ============================================================
     LOAD PAGE
  ============================================================ */
  useEffect(() => {
    const loadEverything = async () => {
      try {
        setLoading(true);

        // 1️⃣ Load form structure FIRST
        const formResult = await getPublicationFormData(
          FORM_STAGE_CODES.PUBLICATION_DETAILS
        );

        if (!formResult.success || !formResult.data) {
          toast.error(formResult.error || "Failed to load form data");
          return;
        }

        const form = formResult.data;
        setFormData(form);

        // Merge all form fields
        const allFields: FormField[] = [
          ...(form.fields || []),
          ...(form.ungroupedFields || []),
          ...(form.subSections?.flatMap((s) => s.fields) || []),
        ].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        // Initialize empty values
        const initValues: Record<string, string> = {};
        allFields.forEach((f) => {
          initValues[f.name] = "";
        });

        let nextValues = initValues;
        let nextHasSavedOnce = false;

        // 2️⃣ If no submissionId → stop here
        if (!submissionId) {
          setValues(nextValues);
          lastSavedValuesRef.current = nextValues;
          setFiles({});
          lastSavedFilesRef.current = {};
          setHasSavedOnce(false);
          setIsDirty(false);
          return;
        }

        // 3️⃣ Load saved submission data
        const submissionResult = await getSubmission(submissionId);

        if (!submissionResult.success || !submissionResult.data) {
          toast.error(
            submissionResult.error || "Failed to load saved submission"
          );
          return;
        }

        const pub = submissionResult.data.publication;
        if (!pub) {
          setValues(nextValues);
          lastSavedValuesRef.current = nextValues;
          setFiles({});
          lastSavedFilesRef.current = {};
          setHasSavedOnce(false);
          setIsDirty(false);
          return;
        }

        // 4️⃣ Prefill publication values
        nextHasSavedOnce = true;
        nextValues = {
          ...nextValues,
          hasDataPublished: pub.hasDataPublished || "",
          typeOfPublication: pub.typeOfPublication || "",
          correspondingAuthorEmail: pub.correspondingAuthorEmail || "",

          publicationTitle: pub.publicationTitle || "",
          publicationLink: pub.publicationLink || "",
          doi: pub.doi || "",
          journal: pub.journal || "",
          volume: pub.volume || "",
          conferenceName: pub.conferenceName || "",
          organizationName: pub.organizationName || "",

          knowPublicationDate: pub.knowPublicationDate || "",
          publicationYear: pub.publicationYear || "",
          publicationMonth: pub.publicationMonth || "",
          publicationDay: pub.publicationDay || "",

          uploadPublicationFile: pub.uploadPublicationFile || "",

          product: pub.product || "",
          relevantStandards: pub.relevantStandards || "",
          epdRegistrationNumber: pub.epdRegistrationNumber || "",

          epdPublicationYear: pub.epdPublicationYear || "",
          epdPublicationMonth: pub.epdPublicationMonth || "",
          epdPublicationDay: pub.epdPublicationDay || "",

          validityYear: pub.validityYear || "",
          validityMonth: pub.validityMonth || "",
          validityDay: pub.validityDay || "",

          geographicScopes: pub.geographicScopes || "",
          country: pub.country || "",
          epdProgramme: pub.epdProgramme || "",
          productCategoryRules: pub.productCategoryRules || "",
          complimentaryPcr: pub.complimentaryPcr || "",
          lcaDatabasesUsed: pub.lcaDatabasesUsed || "",
          lcaToolUsed: pub.lcaToolUsed || "",
          systemBoundaries: pub.systemBoundaries || "",
          cutOffCriteria: pub.cutOffCriteria || "",
          allocationRules: pub.allocationRules || "",

          productionPlantKnown: pub.productionPlantKnown || "",
          productionPlantLatitude: pub.productionPlantLatitude || "",
          productionPlantLongitude: pub.productionPlantLongitude || "",
          productionPlantState: pub.productionPlantState || "",
          productionPlantDistrict: pub.productionPlantDistrict || "",
          productionPlantAddress: pub.productionPlantAddress || "",
          productionPlantPincode: pub.productionPlantPincode || "",

          functionalUnit: pub.functionalUnit || "",
          referenceServiceLife: pub.referenceServiceLife || "",
        };

        const pubRec = pub as Record<string, unknown>;
        for (const key of Object.keys(nextValues)) {
          if (!Object.prototype.hasOwnProperty.call(pubRec, key)) continue;
          const raw = pubRec[key];
          if (raw == null || raw === "") continue;
          if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
            nextValues[key] = String(raw);
          }
        }

        setValues(nextValues);
        lastSavedValuesRef.current = nextValues;
        setFiles({});
        lastSavedFilesRef.current = {};
        setHasSavedOnce(nextHasSavedOnce);
        setIsDirty(false);
      } catch (err: any) {
        console.error("Error loading publication form:", err);
        toast.error(
          err?.message || "An unexpected error occurred while loading the form"
        );
      } finally {
        setLoading(false);
      }
    };

    loadEverything();
  }, [submissionId]);

  // Track dirty state (values + selected files)
  useEffect(() => {
    const savedValues = lastSavedValuesRef.current || {};
    const savedFiles = lastSavedFilesRef.current || {};
    const currentFilesSnap = buildFilesSnapshot(files || {});
    setIsDirty(!(shallowEqual(values || {}, savedValues) && shallowEqual(currentFilesSnap, savedFiles)));
  }, [files, values]);

  /* ============================================================
     CHANGE HANDLER
  ============================================================ */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const name = target.name;

    // FILE input handling (store actual File separately; store file name in `values` for display)
    if (target.type === "file") {
      const file = target.files?.[0] || null;

      if (file && name === "uploadPublicationFile") {
        const sizeError = validatePublicationFileSize(file);
        if (sizeError) {
          target.value = "";
          setFiles((prev) => ({ ...prev, [name]: null }));
          setValues((prev) => ({ ...prev, [name]: "" }));
          setErrors((prev) => ({ ...prev, [name]: sizeError }));
          toast.error(sizeError);
          return;
        }
      }

      setFiles((prev) => ({ ...prev, [name]: file }));
      setValues((prev) => ({ ...prev, [name]: file ? file.name : "" }));

      if (errors[name]) {
        setErrors((prev) => {
          const copy = { ...prev };
          delete copy[name];
          return copy;
        });
      }
      return;
    }

    const value = (e.target as HTMLInputElement | HTMLSelectElement).value;
    setValues((prev) => {
      const next = { ...prev, [name]: value };

      // Auto-populate state and district from latitude/longitude
      if (
        name === "productionPlantLatitude" ||
        name === "productionPlantLongitude"
      ) {
        const latStr = String(next.productionPlantLatitude || "").trim();
        const lngStr = String(next.productionPlantLongitude || "").trim();

        // Only proceed if both lat and lng are provided
        if (!latStr || !lngStr) {
          // Clear state/district if either is missing
          if (!latStr || !lngStr) {
            setValues((p) => ({
              ...p,
              productionPlantState: "",
              productionPlantDistrict: "",
            }));
          }
          return next;
        }

        const lat = Number(latStr);
        const lng = Number(lngStr);

        // Format validation: check if valid numbers and within valid ranges
        const isValidLat =
          Number.isFinite(lat) && lat >= -90 && lat <= 90;
        const isValidLng =
          Number.isFinite(lng) && lng >= -180 && lng <= 180;

        // Only fetch if both are valid
        if (isValidLat && isValidLng) {
          reverseGeocode(lat, lng).then((data) => {
            const address = data?.address || {};

            setValues((p) => ({
              ...p,
              productionPlantState:
                address.state || address.region || address.city || "",
              productionPlantDistrict:
                address.district ||
                address.city_district ||
                address.county ||
                address.state_district ||
                "N/A",
            }));
          }).catch((err) => {
            console.error("Reverse geocoding error:", err);
            // Don't show error to user, just log it
          });
        } else {
          // Clear state/district if format is invalid
          setValues((p) => ({
            ...p,
            productionPlantState: "",
            productionPlantDistrict: "",
          }));
        }
      }

      return next;
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => clearFieldErrorsFromMap(prev, name));
    }
  };

  const clearFile = (fieldName: string) => {
    setFiles((prev) => ({ ...prev, [fieldName]: null }));
    setValues((prev) => ({ ...prev, [fieldName]: "" }));
    if (errors[fieldName]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[fieldName];
        return copy;
      });
    }
  };

  /* ============================================================
     SUBMIT
  ============================================================ */
  const handleSubmit = async (): Promise<boolean> => {
    const submissionIdFinal =
      submissionId || localStorage.getItem("submissionId");

    if (!submissionIdFinal) {
      toast.error("Submission ID is missing");
      return false;
    }

    if (!formData) {
      toast.error("Form data is not available");
      return false;
    }

    try {
      setLoading(true);

      const allFields: FormField[] = [
        ...(formData.fields || []),
        ...(formData.ungroupedFields || []),
        ...(formData.subSections?.flatMap((s: FormSubSection) => s.fields) || []),
      ].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const formErrors = validatePublicationForm(allFields, values, files);
      if (Object.keys(formErrors).length > 0) {
        setErrors(formErrors);
        toast.error(
          getFirstFormError(formErrors) ?? "Please fix the highlighted fields."
        );
        return false;
      }
      setErrors({});

      const publicationFile = files.uploadPublicationFile;
      if (publicationFile) {
        const sizeError = validatePublicationFileSize(publicationFile);
        if (sizeError) {
          setErrors((prev) => ({ ...prev, uploadPublicationFile: sizeError }));
          toast.error(sizeError);
          return false;
        }
      }

      // Build payload dynamically from form fields
      const payload: Record<string, string | File> = {};

      allFields.forEach((field) => {
        if (!field.isEnabled) return;
        if (field.type === "FILE") {
          const f = files[field.name];
          // If no new file chosen, keep existing string value (if any)
          payload[field.name] = f || values[field.name] || "";
          return;
        }
        payload[field.name] = values[field.name] || "";
      });

      const result = await savePublicationDetails(submissionIdFinal, payload);

      if (result.success) {
        toast.success(result.message || "Publication details saved successfully!", {
          style: {
            backgroundColor: "#10b981",
            color: "#ffffff",
            fontSize: "16px",
            fontWeight: "500",
          },
        });
        lastSavedValuesRef.current = values;
        lastSavedFilesRef.current = buildFilesSnapshot(files);
        setHasSavedOnce(true);
        setIsDirty(false);
        return true;
      } else {
        toast.error(result.error || "Failed to save publication details");
        return false;
      }
    } catch (err: any) {
      console.error("Submit error:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "An unexpected error occurred while saving";
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Get all fields for rendering
  const getAllFields = (): FormField[] => {
    if (!formData) return [];
    return [
      ...(formData.fields || []),
      ...(formData.ungroupedFields || []),
      ...(formData.subSections?.flatMap((s: FormSubSection) => s.fields) || []),
    ].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  };

  return {
    formData,
    values,
    hasSavedOnce,
    isDirty,
    loading,
    errors,
    handleChange,
    clearFile,
    handleSubmit,
    getAllFields,
  };
};

